import {
    CollisionEventType,
    Component,
    Mesh,
    MeshComponent,
    PhysXComponent,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Trail} from '@wonderlandengine/components';

import {vec3} from 'gl-matrix';
import {TeamMaterials} from './utils/team.js';
import {Team} from '../server/types.js';
import {AudioManager, globalAudioManager} from '@wonderlandengine/spatial-audio';
import {Sounds} from './sounds.js';

const tempVec = vec3.create();
export const BulletScaling = [0.01, 0.01, 0.01];
export const SplatScaling = [0.15, 0.15, 0.15];

/**
 * bullet
 */
export class Bullet extends Component {
    static TypeName = 'bullet';

    direction = vec3.create();

    offline = false;

    @property.mesh({required: true})
    splatMesh!: Mesh;

    bulletMesh!: Mesh;

    start() {
        this.bulletMesh = this.object.getComponent(MeshComponent)!.mesh!;

        const physx = this.object.getComponent(PhysXComponent)!;
        physx.active = true;
        physx.onCollision((type, other) => {
            if (type == CollisionEventType.TouchLost) return;
            if (other.object.name !== 'LevelCollision') return;
            if (!this.offline) {
                /* Stop bouncing around, until shoot-rem event */
                setTimeout(
                    () => (this.object.getComponent(PhysXComponent)!.active = false),
                    0
                );
            } else {
                /* Since splat() deactivates physics, we can't do it in the onCollision callback */
                setTimeout(() => this.splat(), 0);
            }
        });

        this.object.getComponent(MeshComponent)!.active = true;
    }

    onActivate() {
        const physx = this.object.getComponent(PhysXComponent)!;
        physx.active = true;
        this.object.getComponent(Trail)!.active = true;

        vec3.add(tempVec, this.direction, this.object.getPositionWorld(tempVec));
        this.object.lookAt(tempVec);
        this.object.rotateAxisAngleDegObject([1, 0, 0], 90);
        this.object.rotateAxisAngleDegObject([0, 1, 0], Math.random() * 360);

        physx.linearVelocity = this.direction;
    }

    updateFromServer(pos: number[], dir: number[], team: Team) {
        const physx = this.object.getComponent(PhysXComponent)!;
        physx.active = false;
        this.object.setPositionLocal(pos);

        this.direction[0] = dir[0];
        this.direction[1] = dir[1];
        this.direction[2] = dir[2];

        physx.active = true;
        physx.linearVelocity = this.direction;
        this.updateMaterial(team);
    }

    updateMaterial(team: Team) {
        const mat = TeamMaterials[team];
        if (!mat) return;
        this.object.getComponent(MeshComponent)!.material = mat;
        const trail = this.object.getComponent(Trail)!;
        /* FIXME: We're depending on internals */
        const mesh = (trail as any)._meshComp!;
        if (mesh) mesh.material = mat;
    }

    onDeactivate(): void {
        const p = this.object.getComponent(PhysXComponent);
        if (p) p.active = false;

        const t = this.object.getComponent(Trail);
        if (t) {
            setTimeout(() => {
                if (!t.isDestroyed) t.active = false;
            }, 500);
        }
    }

    splat(impactNormal?: number[]) {
        this.object.setScalingLocal(SplatScaling);
        this.object.getComponent(MeshComponent)!.mesh = this.splatMesh;

        this.object.getPositionWorld(tempVec);
        (globalAudioManager as AudioManager).play(Sounds.Splat, {
            position: tempVec as Float32Array,
        });

        if (impactNormal) {
            vec3.sub(tempVec, tempVec, impactNormal as vec3);
            this.object.lookAt(tempVec);
            this.object.rotateAxisAngleDegObject([1, 0, 0], 90);
            this.object.rotateAxisAngleDegObject([0, 1, 0], Math.random() * 360);
        }

        this.active = false;
    }

    resetBullet() {
        /* Has never been activated yet */
        if (!this.bulletMesh) return;

        /* Bullet might be attached to player */
        this.object.parent = null;

        this.object.getComponent(Trail)!.resetTrail();
        this.object.getComponent(MeshComponent)!.mesh = this.bulletMesh;
        this.object.setScalingLocal(BulletScaling);
        /* Should already be deactivated in splat(), but if the pool is exhausted or
         * the bullet shot out of the level, we might not have splat yet */
        this.active = false;
    }
}
